import React from "react";
import { Helmet } from "react-helmet-async";
import { BRAND_FULL, BRAND_NAME, BRAND_DESCRIPTION, SEO_DEFAULTS } from "../config/branding";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  article?: {
    publishedTime: string;
    author: string;
    section: string;
  };
}

/**
 * SEO Component - Manages meta tags, Open Graph, Twitter Cards, and JSON-LD
 * Use this component in any page to override default SEO values
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description = BRAND_DESCRIPTION,
  keywords = SEO_DEFAULTS.keywords,
  canonicalUrl = SEO_DEFAULTS.canonicalUrl,
  ogImage = SEO_DEFAULTS.ogImage,
  ogType = "website",
  article,
}) => {
  const pageTitle = title ? `${title} | ${BRAND_FULL}` : SEO_DEFAULTS.title;

  // JSON-LD structured data for SoftwareApplication
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: BRAND_NAME,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web Browser",
    description: BRAND_DESCRIPTION,
    url: SEO_DEFAULTS.canonicalUrl,
    image: SEO_DEFAULTS.ogImage,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={BRAND_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content={SEO_DEFAULTS.twitterHandle} />

      {/* Article specific Open Graph (when article is provided) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.section} />
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default SEO;
